/**
 * Shared concurrency gate for Limits API /view/pass|zone|tilt calls.
 *
 * Those endpoints are slow (~10–20s each). Opening WPS drilldown used to fire
 * many welds × 3 views in parallel; past ~6–8 in-flight requests the API Gateway
 * hits its ~29s integration timeout (504). Gateway 504s omit CORS headers, so
 * the browser reports a CORS error instead of the timeout.
 *
 * Cap in-flight view GETs globally across batch + row-select callers.
 */
export const LIMITS_VIEW_MAX_IN_FLIGHT = 3;

/**
 * Background prefetch keeps one slot free so clicking a weld can start
 * immediately instead of waiting behind the batch (and then timing out).
 */
export const LIMITS_VIEW_BACKGROUND_MAX = 2;

/** Client abort before API GW 504 so failures surface as timeouts, not CORS. */
export const LIMITS_VIEW_REQUEST_TIMEOUT_MS = 28_000;

let inFlight = 0;
let waitSeq = 0;

interface Waiter {
  priority: number;
  seq: number;
  resume: () => void;
}

const waitQueue: Waiter[] = [];

function pickNextWaiter(): Waiter | undefined {
  if (waitQueue.length === 0) return undefined;
  let best = 0;
  for (let i = 1; i < waitQueue.length; i += 1) {
    const candidate = waitQueue[i];
    const current = waitQueue[best];
    if (
      candidate.priority < current.priority ||
      (candidate.priority === current.priority && candidate.seq < current.seq)
    ) {
      best = i;
    }
  }
  return waitQueue.splice(best, 1)[0];
}

function canStart(priority: number): boolean {
  if (priority <= 0) return inFlight < LIMITS_VIEW_MAX_IN_FLIGHT;
  return inFlight < LIMITS_VIEW_BACKGROUND_MAX;
}

function blockedByQueue(priority: number): boolean {
  return waitQueue.some((waiter) => waiter.priority <= priority);
}

function acquire(priority: number): Promise<void> {
  if (canStart(priority) && !blockedByQueue(priority)) {
    inFlight += 1;
    return Promise.resolve();
  }
  return new Promise<void>((resolve) => {
    waitQueue.push({
      priority,
      seq: waitSeq,
      resume: () => {
        inFlight += 1;
        resolve();
      },
    });
    waitSeq += 1;
  });
}

function release(): void {
  inFlight -= 1;
  const next = pickNextWaiter();
  if (next && canStart(next.priority)) {
    next.resume();
    return;
  }
  if (next) {
    waitQueue.unshift(next);
  }
}

/**
 * Run `fn` when a slot is free; releases the slot when `fn` settles.
 * Lower `priority` runs first. Interactive weld opens use 0; background batches use 1.
 */
export async function withLimitsViewSlot<T>(
  fn: () => Promise<T>,
  priority = 1,
): Promise<T> {
  await acquire(priority);
  try {
    return await fn();
  } finally {
    release();
  }
}

/** Test helper — resets queue state between cases. */
export function __resetLimitsViewFetchQueueForTests(): void {
  inFlight = 0;
  waitSeq = 0;
  waitQueue.length = 0;
}

/** Test helper — current in-flight count (not waiting). */
export function __limitsViewInFlightForTests(): number {
  return inFlight;
}
